import React, { useContext, useEffect, useMemo, useState } from "react";

import { Redirect, Route, Switch, useParams } from "react-router-dom";

import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import { NoMatchRoute, RouteElement } from "@components/navigation";
import {
	HeaderTargetType,
	HeaderType,
} from "@data-types/layout/headerTypeEnum";
import { TSection } from "@data-types/offer/live/Section";
import LiveEventRouteLayout from "@features/offer/desktop/layout/LiveEventRouteLayout";
import { LiveOfferMenu } from "@features/offer/desktop/offer-menu";
import LivePage from "@features/offer/desktop/pages/live/LivePage";
import { AsideWidgets } from "@features/side-widgets";
import { LiveStatus } from "@gp/models";
import { useRootAppStore } from "@hooks";
import { AdditionalOfferContainerRefContext } from "@state/context/additional-offer/AdditionalOfferContainerRefContext";
import { SetIsAdditionalOfferOpenCtxProviderForParentLayout } from "@state/context/additional-offer/SetIsAdditionalOfferOpenCtxProviderForParentLayout";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";
import { useFavoritesCounter } from "@state/hooks/offer/useFavoritesCounter";
import { LiveOfferMenuStore } from "@state/stores/offer/live/LiveOfferMenuStore";
import classNames from "classnames";
import { observer } from "mobx-react";

function LiveRouteLayout() {
	const [additionalOfferContainerRef, setAdditionalOfferContainerRef] =
		useState<HTMLDivElement | null>(null);
	const offerStore = useRootAppStore();
	const { isSideMenuExpanded } = offerStore;

	const [isAdditionalOfferVisible, setIsAdditionalOfferOpen] =
		React.useState(false);
	const rootStore = useRootAppStore();

	const favoriteStore = useContext(FavoritesStoreContext);
	const { period } = useParams<{ period?: string }>();

	const liveOfferMenuStore = useMemo(
		() => new LiveOfferMenuStore(rootStore, favoriteStore),
		[]
	);

	useFavoritesCounter(LiveStatus.LIVE);

	useEffect(() => {
		rootStore.betSlipStore.playedLiveEventsStore =
			liveOfferMenuStore.liveEventsFromMyBets;

		rootStore.quickPlayStore.quickBetStore.initializeMenuStore(
			liveOfferMenuStore
		);
		return () => {
			rootStore.betSlipStore.playedLiveEventsStore = undefined;
			rootStore.quickPlayStore.quickBetStore.removeMenuStore();
		};
	});

	//#region css classes

	const mainclassNames = classNames("l--main l--main--live", {
		"sidebar-left-expanded": isSideMenuExpanded,
		"sidebar-left-collapsed": !isSideMenuExpanded,
	});

	const contentClassNames = classNames("l--content", {
		"additional-is-hidden": !isAdditionalOfferVisible,
		"additional-is-visible": isAdditionalOfferVisible,
		"sidebar-left-expanded": isSideMenuExpanded,
		"sidebar-left-collapsed": !isSideMenuExpanded,
	});

	const additionalClassNames = classNames("l--content__additional", {
		"is-visible": isAdditionalOfferVisible,
		"is-hidden": !isAdditionalOfferVisible,
		"sidebar-left-expanded": isSideMenuExpanded,
		"sidebar-left-collapsed": !isSideMenuExpanded,
	});

	const footerClassNames = classNames("l--footer", {
		"sidebar-left-expanded": isSideMenuExpanded,
	});

	const asideLeftClasses = classNames("l--aside--left", {
		"is-expanded": isSideMenuExpanded,
	});

	//#endregion css classes

	return (
		<SetIsAdditionalOfferOpenCtxProviderForParentLayout.Provider
			value={{
				setIsAdditionalOpen: setIsAdditionalOfferOpen,
				isAdditionalOpen: isAdditionalOfferVisible,
			}}
		>
			<FavoritesStoreContext.Provider value={favoriteStore}>
				<Switch>
					{/* Legacy Route Redirect */}
					<Route
						path="/:culture/live/:period/event/:eventId"
						render={({ match }) => (
							<Redirect
								to={`/${match.params.culture}/live/${match.params.period}/${match.params.eventId}`}
							/>
						)}
					/>
					<RouteElement
						exact
						path={[
							"/:culture/live/:period",
							"/:culture/live/:period/f/:ids*",
						]}
						privateRoute={[
							TSection.MyFavoritesEvents,
							TSection.MyFavoritesTournaments,
						].includes(period as TSection)}
					>
						<DesktopLayout
							headerType={HeaderType.WITH_SIDEBAR}
							headerActions={(target) => {
								if (target == HeaderTargetType.LIVE) {
									liveOfferMenuStore.menu.onCheck(false);
									liveOfferMenuStore.deselectSport();
								}
							}}
							mainClassName={mainclassNames}
							footerClassName={footerClassNames}
						>
							<aside className={asideLeftClasses}>
								<LiveOfferMenu
									liveOfferMenuStore={liveOfferMenuStore}
								/>
							</aside>

							<section className={contentClassNames}>
								<AdditionalOfferContainerRefContext.Provider
									value={additionalOfferContainerRef}
								>
									<LivePage
										liveOfferMenuStore={liveOfferMenuStore}
									/>
									<div
										ref={(newRef: HTMLDivElement | null) =>
											setAdditionalOfferContainerRef(
												newRef
											)
										}
										id="additional-offer-container"
										className={additionalClassNames}
									/>
								</AdditionalOfferContainerRefContext.Provider>
							</section>

							<AsideWidgets />
						</DesktopLayout>
					</RouteElement>

					<RouteElement
						exact
						path="/:culture/live/:period/:eventIds+"
					>
						<LiveEventRouteLayout
							liveOfferMenuStore={liveOfferMenuStore}
						/>
					</RouteElement>
					<NoMatchRoute />
				</Switch>
			</FavoritesStoreContext.Provider>
		</SetIsAdditionalOfferOpenCtxProviderForParentLayout.Provider>
	);
}

export default observer(LiveRouteLayout);
